import { useState, useCallback, type ReactNode } from 'react';
import { uploadApi } from '../api/client';

// ===== Toast =====
let toastEl: HTMLElement | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

export function showToast(msg: string) {
  if (toastTimer) clearTimeout(toastTimer);
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'toast-admin';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = msg;
  toastEl.style.display = 'block';
  toastEl.style.animation = 'none';
  toastEl.offsetHeight;
  toastEl.style.animation = 'toast-down 0.25s';
  toastTimer = setTimeout(() => {
    if (toastEl) toastEl.style.display = 'none';
  }, 2200);
}

// ===== Confirm Dialog =====
export function ConfirmDialog({
  title, body, onConfirm, onCancel, danger,
}: {
  title: string;
  body: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  return (
    <div className="modal-mask" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className={`modal-title ${danger ? 'danger' : ''}`}>{title}</div>
        <div className="modal-body">{body}</div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel}>取消</button>
          <button className="btn btn-primary" onClick={onConfirm} style={danger ? { background: 'var(--danger)' } : {}}>
            确认
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== Drawer =====
export function Drawer({
  title, children, footer, onClose, width,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  return (
    <>
      <div className="drawer-mask" onClick={onClose} />
      <div className="drawer" style={width ? { width } : undefined}>
        <div className="drawer-header">
          <span>{title}</span>
          <button className="btn-icon" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M4 4L14 14M14 4L4 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-footer">{footer}</div>}
      </div>
    </>
  );
}

// ===== Status Badge =====
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    sale: { label: '在售', cls: 'sale' },
    pending: { label: '待上架', cls: 'pending' },
    hidden: { label: '已隐藏', cls: 'hidden' },
    empty: { label: '空槽位', cls: 'empty' },
  };
  const item = map[status] || map.empty;
  return <span className={`status-badge ${item.cls}`}>{item.label}</span>;
}

// ===== Badge Chip (toggle) =====
export function BadgeChip({
  type, active, onToggle,
}: {
  type: 'hot' | 'new' | 'limit';
  active: boolean;
  onToggle: () => void;
}) {
  const labels = { hot: 'Hot', new: 'New', limit: 'Limit' };
  return (
    <span className={`chip ${type} ${active ? 'active' : ''}`} onClick={onToggle}>
      {labels[type]}
    </span>
  );
}

// ===== Image Uploader =====
export function ImageUploader({
  files, onAdd, onRemove, multiple = false,
}: {
  files: { url: string }[];
  onAdd: (url: string) => void;
  onRemove: (index: number) => void;
  multiple?: boolean;
}) {
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < selected.length; i++) {
        const url = await uploadApi.upload(selected[i]);
        onAdd(url);
      }
      showToast('上传成功');
    } catch (err) {
      showToast(err instanceof Error ? err.message : '上传失败');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [onAdd]);

  return (
    <div>
      <label className="upload-area" style={uploading ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
        {uploading ? '上传中...' : (multiple ? '点击上传图片（可多选）' : '点击上传图片')}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple={multiple}
          onChange={handleFile}
          style={{ display: 'none' }}
        />
      </label>
      {files.length > 0 && (
        <div className="upload-preview">
          {files.map((f, i) => (
            <div key={i} className="upload-thumb">
              <img src={f.url} alt="" />
              <button className="remove" onClick={() => onRemove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Format Price =====
export function formatPrice(price: number): string {
  return price.toLocaleString('en-US');
}

// ===== Empty State =====
export function EmptyState({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="empty-admin">
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" opacity="0.3">
        <rect x="10" y="14" width="28" height="26" rx="4" stroke="currentColor" strokeWidth="2" />
        <path d="M18 14V9h12v5" stroke="currentColor" strokeWidth="2" />
      </svg>
      <div className="title">{title}</div>
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

// ===== Skeleton =====
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: '1px solid var(--line-2)' }}>
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="skel" style={{ width: `${100 / cols}%`, height: 20 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="stat-grid">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="stat-card">
          <div className="skel" style={{ width: 80, height: 14 }} />
          <div className="skel" style={{ width: 60, height: 30, marginTop: 8 }} />
        </div>
      ))}
    </div>
  );
}
